import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, Loader2, LogIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      
      if (!res.ok) {
        let detail = null;
        try {
          const errorData = await res.json();
          detail = errorData.detail;
        } catch (_) {}
        if (detail) {
          throw new Error(detail);
        } else if (res.status === 401) {
          throw new Error("Forkert email eller password");
        } else {
          throw new Error("Server fejl - prøv igen");
        }
      }
      
      const data = await res.json();
      login(data.token, data.user, rememberMe);
    } catch (err) {
      setError(err.message || "Noget gik galt. Prøv igen.");
    } finally {
      setLoading(false);
    }
  };

  const MAINTENANCE = false;

  if (MAINTENANCE) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm text-center space-y-4">
          <h1 className="text-3xl font-extrabold tracking-tighter font-[Manrope]">
            <span className="text-[#FF2A6D]">Beauty & Style</span>
            <span className="text-foreground ml-1.5">Hub</span>
          </h1>
          <div className="p-5 rounded-2xl border border-amber-500/30 bg-amber-500/10">
            <p className="font-semibold text-amber-700 dark:text-amber-400 text-base">Vi opdaterer i øjeblikket systemet</p>
            <p className="text-muted-foreground text-sm mt-1">Vi er snart tilbage.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold tracking-tighter font-[Manrope]" data-testid="login-title">
            <span className="text-[#FF2A6D]">Beauty & Style</span>
            <span className="text-foreground ml-1.5">Hub</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            Log ind med din Podio-konto
          </p>
        </div>

        <Card className="bg-card border-border shadow-none" data-testid="login-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-bold">Log ind</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label-overline text-muted-foreground block mb-2">
                  Podio Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="din@email.dk"
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  inputMode="email"
                  className="w-full h-12 px-4 rounded-lg bg-background border border-border text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/50 focus:border-[#FF2A6D] transition-all"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <label className="label-overline text-muted-foreground block mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Dit Podio password"
                  required
                  className="w-full h-12 px-4 rounded-lg bg-background border border-border text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/50 focus:border-[#FF2A6D] transition-all"
                  data-testid="login-password-input"
                />
              </div>

              {error && (
                <div
                  className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                  data-testid="login-error"
                >
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <label className="flex items-center gap-2 cursor-pointer select-none pt-1" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-border bg-background text-[#FF2A6D] focus:ring-[#FF2A6D]/50 cursor-pointer accent-[#FF2A6D]"
                  data-testid="remember-me-checkbox"
                />
                <span className="text-sm text-muted-foreground">Husk mig</span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#FF2A6D]/90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <LogIn className="w-4 h-4" />
                )}
                {loading ? "Logger ind..." : "Log ind med Podio"}
              </button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-[13px] text-muted-foreground mt-6">
          Brug samme login som din Podio-konto
        </p>
      </div>
    </div>
  );
}
